import React, { useState, useEffect } from 'react';
import { FormFieldProps } from '../../FormTypes';
import { IPayFormFields, IPayScale } from 'parent-portal/forms/JobRequestTypes';
import { Text, Colors, OutlinedTextInput } from 'library';
import PaySlider from '../../PaySlider';
import { useFieldValidation } from '../../FormHook';
import { Grid } from '@mui/material';
import { useUserContext } from 'UserContext';
import { getPricingPlan, getTotalHourlyAmountWithFee } from 'shared';

interface PayPageProps {
    pay: FormFieldProps<IPayFormFields>;
    payScales: FormFieldProps<IPayScale[]>;
    defaultPayScales: IPayScale[];
    updateDefaultPayScales: (value: IPayScale[]) => void;
}

export function payRangeInfoValid(payScales: IPayScale[]) {
    if (payScales.length === 1) {
        return true;
    }
    return payScales.every((x) => x.description);
}

export default function BusinessPayInputs({ pay, payScales, defaultPayScales, updateDefaultPayScales }: PayPageProps) {
    const { user } = useUserContext();
    const [payMin, setPayMin] = useState(pay.value.payMin);
    const [payMax, setPayMax] = useState(pay.value.payMax);

    useFieldValidation(pay);

    useEffect(() => {
        pay.setValue({
            payMin,
            payMax,
            payFixed: null,
            isFixedPay: false,
        });
        payScales.setValue(
            [...Array.from({ length: payMax - payMin + 1 })].map((x, i) => {
                return {
                    rate: i + payMin,
                    description: getPayScaleDescription(i + payMin),
                };
            }),
        );
    }, [payMin, payMax]);

    function getPayScaleDescription(rate: number) {
        return (
            payScales.value.find((scale) => scale.rate === rate)?.description ||
            defaultPayScales.find((scale) => scale.rate === rate)?.description ||
            ''
        );
    }

    function onPayRateChange(event: any, newValue: number | number[]) {
        if (typeof newValue === 'number') {
            setPayMin(newValue);
            setPayMax(newValue);
        } else {
            setPayMin(newValue[0]);
            setPayMax(newValue[1]);
        }
    }

    const pricingPlan = getPricingPlan(user);
    const minTotalWithFee = getTotalHourlyAmountWithFee(pay.value.payMin, pricingPlan);
    const maxTotalWithFee = getTotalHourlyAmountWithFee(pay.value.payMax, pricingPlan);

    function onPayScaleChange(description: string, index: number) {
        payScales.value.splice(index, 1, { ...payScales.value[index], description });
        const newPayScales = [...payScales.value];
        payScales.setValue(newPayScales);
        updateDefaultPayScales(newPayScales);
    }

    const isChildCare = user?.businesses?.[0]?.business_type === 'CHILD_CARE';
    const placeholderText = `Ex. 3 Years/exp, ${isChildCare ? 'CDA, ' : ''}Associate's, Bachelor's`;

    return (
        <>
            <Grid container item style={{ width: '75%' }}>
                <Text color={Colors.turquoise} bold textStyle={{ marginBottom: 20, marginTop: 30 }}>
                    Pay
                </Text>
                <PaySlider
                    payRateTip={`Enter the rate you want to pay the worker before Tandem's fee. Your total pay will be $${minTotalWithFee.toFixed(
                        2,
                    )}/hr - $${maxTotalWithFee.toFixed(2)}/hr with this scale`}
                    value={[pay.value.payMin, pay.value.payMax]}
                    onPayRateChange={onPayRateChange}
                    isRange={true}
                />
            </Grid>
            <Grid container item style={{ marginTop: 10, marginBottom: 30 }}>
                <Grid style={{ marginBottom: 10, width: '100%' }}>
                    {payScales.value.map((x, i) => (
                        <Grid key={x.rate} container direction="row" alignItems="center" style={{ padding: 10 }}>
                            <Text textStyle={{ marginRight: 5 }}>${x.rate}:</Text>
                            <OutlinedTextInput
                                value={x.description}
                                onChange={(e) => onPayScaleChange(e.target.value, i)}
                                style={{ flexGrow: 1 }}
                                placeholder={placeholderText}
                            />
                        </Grid>
                    ))}
                    {!payRangeInfoValid(payScales.value) ? (
                        <Text variant="caption" textStyle={{ color: 'red' }}>
                            Details required for each value in the pay range
                        </Text>
                    ) : null}
                </Grid>
                <Text variant="caption" bold>
                    We will use this information to set the pay rate for the shifts we fill, so please be specific about
                    your requirements for qualifications and experience for each rate.
                </Text>
            </Grid>
        </>
    );
}
